import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import ProtectedRoute from './ProtectedRoute';
import { isJwtExpired } from '../utils/isJwtExpired';
import {
  ACCOUNT_MANAGEMENT_ROUTE,
  CHANGE_PASSWORD_ROUTE,
  CLICK_AND_COLLECT_BAYS_ROUTE,
  CLICK_AND_COLLECT_CNC_DASHBOARD_ROUTE,
  CLICK_AND_COLLECT_COLLECTION_POINTS_ROUTE,
  CLICK_AND_COLLECT_GATE_CHECKIN_ROUTE,
  CLICK_AND_COLLECT_WAREHOUSES_ROUTE,
  CREATE_HOMESCREEN_BANNER_ROUTE,
  CREATE_USER_ROUTE,
  CUSTOMER_APP_ROUTE,
  CUSTOMER_SETTINGS_ROUTE,
  CUSTOMER_ACCOUNT_REACTIVATION_ROUTE,
  CUSTOMER_ACCOUNT_MANAGEMENT_ROUTE,
  DASHBOARD_ROUTE,
  DELIVERY_SLOT_ROUTE,
  DELIVERY_TYPES_ROUTE,
  EDIT_HOMESCREEN_BANNER_ROUTE,
  ETA_STATUS_ROUTE,
  FLASHDEALS_REORDER_ROUTE,
  SECTION_REORDER_ROUTE,
  HOMESCREEN_BANNER_ROUTE,
  INVENTORY_ROUTE,
  lOGIN_ROUTE,
  LOGISTICS_ORDER_DETAILS,
  NAVIGATION_ROUTE,
  OTP_PROVIDERS_ROUTE,
  PAGE_NOT_FOUND_ROUTE,
  ROLE_ACCESS_AND_PERMISSIONS_ROUTE,
  ORDERS_ROUTE,
  SALES_ROUTE,
  SETTING_ROUTE,
  USERS_ROUTE,
  CUSTOMER_REFUND_REQUEST_ROUTE,
  PRODUCT_SYNC_ROUTE,
  ANALYTICS_FRESH_PRODUCE_ROUTE,
  ANALYTICS_REPORT_ROUTE,
  AUDIT_LOG_ROUTE,
  ORDER_ERRORS_ROUTE,
  ORDER_RERUNS_ROUTE,
  NAVIGATION_AUDIT_REGEX_ROUTE,
  SECTION_SCHEDULER_ROUTE,
  PRODUCT_SECTION_SETTINGS_ROUTE,
  STOCKTAKE_ROUTE,
  STOCKTAKE_APPROVALS_ROUTE,
  FAST_MOVING_SKU_ROUTE,
  ORDERS_TO_PICK_ROUTE,
  PACKED_ORDERS_ROUTE,
  PRODUCT_STOCK_SUBSCRIPTIONS_ROUTE,
} from '../constants/routes';
import { restoreSession, logout, setUserNavigation } from '../store/Reducers/UserSlice';
import { setRolesAndPermissions } from '../store/Reducers/RolesAndPermissionsSlice';
import { setPermissions } from '../store/Reducers/PermissionsSlice';
import { setAdminUsers } from '../store/Reducers/AdminUsersSlice';
import { SessionService } from '../api/services/sessionservice';
import { IAdminUser } from '../types/store';
import ResponsiveDrawer from '../components/Drawer/Drawer';
import { AccountManagementPage } from '../pages/AccountManagement';
import { CreateUserPage } from '../pages/CreateUser';
import { CustomerAppPage } from '../pages/CustomerApp';
import { CustomerSettingPage } from '../pages/CustomerSetting';
import { CustomerAccountReactivationPage } from '../pages/CustomerAccountReactivation';
import { CustomerAccountManagementPage } from '../pages/CustomerAccountManagement';
import { DashboardPage } from '../pages/Dashboard';
import { DeliverySlotPage } from '../pages/DeliverySlot';
import { ETAStatusPage } from '../pages/ETAStatus';
import { FlashdealReorderSequence } from '../pages/FlashdealReorderSequence';
import { SectionReorderSequence } from '../pages/SectionReorderSequence';
import { HomescreenBannerPage } from '../pages/HomescreenBanner';
import { InventoryPage } from '../pages/Inventory';
import { LoginPage } from '../pages/Login';
import { LogisticsOrderDetails } from '../pages/LogisticsOrderDetails';
import { PageNotFound } from '../pages/PageNotFound';
import { RoleAccessAndPermissionsPage } from '../pages/RoleAccessAndPermissions';
import { SalesPage } from '../pages/Sales';
import { SettingsPage } from '../pages/Setting';
import { UsersPage } from '../pages/Users';
import { IRootState } from '../types/store';
import { NavigationPage } from '../pages/Navigation';
import { WarehousePage } from '../pages/Warehouse';
import { StocktakeApprovalsPage } from '../pages/StocktakeApprovals';
import { CreateEditHomescreenBannerPage } from '../pages/CreateEditHomescreenBanner';
import { ChangePasswordPage } from '../pages/ChangePassword';
import { CustomerRefundRequestPage } from '../pages/CustomerRefundRequest';
import { AuditLogPage } from '../pages/AuditLog';
import { OrderErrorsPage } from '../pages/OrderErrors';
import { OrderRerunsPage } from '../pages/OrderReruns';
import { FreshProducePage } from '../pages/Analytics/FreshProduce';
import { AnalyticsReportPage } from '../pages/Analytics/Report';
import { NavigationAuditRegexPage } from '../pages/NavigationAuditRegex';
import { OrdersPage } from '../pages/Orders';
import { SectionSchedulerPage } from '../pages/SectionScheduler';
import { ProductSectionSettingsPage } from '../pages/ProductSectionSettings';
import { WarehouseManagementPage } from '../pages/WarehouseManagement';
import { BayManagementPage } from '../pages/BayManagement';
import { CollectionPointManagementPage } from '../pages/CollectionPointManagement';
import { GateCheckInPage } from '../pages/GateCheckIn';
import { DeliveryTypesPage } from '../pages/DeliveryTypes';
import { OtpProvidersPage } from '../pages/OtpProviders';
import { CncDashboardPage } from '../pages/CncDashboard';
import { FastMovingSkuPage } from '../pages/FastMovingSku';
import { OrdersToPickPage } from '../pages/OrdersToPick';
import { PackedOrdersPage } from '../pages/PackedOrders';
import { ProductStockSubscriptionsPage } from '../pages/ProductStockSubscriptions';

const RootRoutes: React.FC = () => {
  const dispatch = useDispatch();
  const [checkingSession, setCheckingSession] = useState(true);
  const { isLoggedIn, passwordExpired } = useSelector((state: IRootState) => state.user);

  useEffect(() => {
    const token = localStorage.getItem('ss_adm_pnl_access_token');
    const user = localStorage.getItem('ss_adm_pnl_user');
    const menuItems = localStorage.getItem('ss_adm_pnl_menu_items');
    const restoredUser: IAdminUser | null = user ? JSON.parse(user) : null;

    if (
      token &&
      !isJwtExpired(token) &&
      menuItems &&
      restoredUser &&
      Array.isArray(restoredUser.roles)
    ) {
      dispatch(restoreSession({ user: user as string, menuItems: menuItems }));

      SessionService.init(restoredUser.roles.map((role) => role.name))

        .then((init) => {
          if (init.navigationItems) {
            localStorage.setItem(
              'ss_adm_pnl_menu_items',
              JSON.stringify(init.navigationItems.navigation),
            );
            dispatch(setUserNavigation(init.navigationItems.navigation));
          }
          if (init.rolesAndPermissions) {
            dispatch(setRolesAndPermissions(init.rolesAndPermissions));
          }
          if (init.permissions) {
            dispatch(setPermissions(init.permissions));
          }
          if (init.adminUsers) {
            dispatch(setAdminUsers(init.adminUsers));
          }
        })
        .catch((err) => {
          console.error('Failed to refresh session data on restore', err);
        });
    } else {
      dispatch(logout());
      localStorage.clear();
    }

    setCheckingSession(false);
  }, [dispatch]);

  if (checkingSession) return null; // or loader
  console.log({ isLoggedIn, passwordExpired });
  return (
    <Routes>
      {/* Unprotected routes */}
      <Route path={lOGIN_ROUTE} element={<LoginPage />} />
      <Route path={`${LOGISTICS_ORDER_DETAILS}/:orderNumber`} element={<LogisticsOrderDetails />} />
      <Route path={PAGE_NOT_FOUND_ROUTE} element={<PageNotFound />} />

      {/* Protected routes */}
      <Route element={<ProtectedRoute />}>
        <Route element={<ResponsiveDrawer />}>
          <Route path={DASHBOARD_ROUTE} element={<DashboardPage />} />
          <Route path={ACCOUNT_MANAGEMENT_ROUTE} element={<AccountManagementPage />} />
          <Route
            path={CUSTOMER_ACCOUNT_REACTIVATION_ROUTE}
            element={<CustomerAccountReactivationPage />}
          />
          <Route
            path={CUSTOMER_ACCOUNT_MANAGEMENT_ROUTE}
            element={<CustomerAccountManagementPage />}
          />
          <Route path={`${INVENTORY_ROUTE}/:section?`} element={<InventoryPage />} />
          <Route path={PRODUCT_SYNC_ROUTE} element={<InventoryPage />} />
          <Route path={`${CUSTOMER_APP_ROUTE}/:section?`} element={<CustomerAppPage />} />
          <Route path={FLASHDEALS_REORDER_ROUTE} element={<FlashdealReorderSequence />} />
          <Route path={SECTION_REORDER_ROUTE} element={<SectionReorderSequence />} />
          <Route path={`${DELIVERY_SLOT_ROUTE}/:section?`} element={<DeliverySlotPage />} />
          <Route path={ORDERS_ROUTE} element={<OrdersPage />} />
          <Route path={`${SALES_ROUTE}/:section?`} element={<SalesPage />} />
          <Route path={`${USERS_ROUTE}/:section?`} element={<UsersPage />} />
          <Route path={SETTING_ROUTE} element={<SettingsPage />} />
          <Route path={CUSTOMER_SETTINGS_ROUTE} element={<CustomerSettingPage />} />
          <Route path={HOMESCREEN_BANNER_ROUTE} element={<HomescreenBannerPage />} />
          <Route
            path={CREATE_HOMESCREEN_BANNER_ROUTE}
            element={<CreateEditHomescreenBannerPage />}
          />
          <Route path={EDIT_HOMESCREEN_BANNER_ROUTE} element={<CreateEditHomescreenBannerPage />} />
          <Route path={ETA_STATUS_ROUTE} element={<ETAStatusPage />} />
          <Route
            path={`${ROLE_ACCESS_AND_PERMISSIONS_ROUTE}/:section?`}
            element={<RoleAccessAndPermissionsPage />}
          />
          <Route path={CREATE_USER_ROUTE} element={<CreateUserPage />} />
          <Route path={NAVIGATION_ROUTE} element={<NavigationPage />} />
          <Route path={CHANGE_PASSWORD_ROUTE} element={<ChangePasswordPage />} />
          <Route path={CUSTOMER_REFUND_REQUEST_ROUTE} element={<CustomerRefundRequestPage />} />
          <Route path={AUDIT_LOG_ROUTE} element={<AuditLogPage />} />
          <Route path={ORDER_ERRORS_ROUTE} element={<OrderErrorsPage />} />
          <Route path={ORDER_RERUNS_ROUTE} element={<OrderRerunsPage />} />
          <Route path={ANALYTICS_FRESH_PRODUCE_ROUTE} element={<FreshProducePage />} />
          <Route path={ANALYTICS_REPORT_ROUTE} element={<AnalyticsReportPage />} />
          <Route path={NAVIGATION_AUDIT_REGEX_ROUTE} element={<NavigationAuditRegexPage />} />
          <Route path={SECTION_SCHEDULER_ROUTE} element={<SectionSchedulerPage />} />
          <Route path={PRODUCT_SECTION_SETTINGS_ROUTE} element={<ProductSectionSettingsPage />} />
          <Route path={STOCKTAKE_ROUTE} element={<WarehousePage />} />
          <Route path={STOCKTAKE_APPROVALS_ROUTE} element={<StocktakeApprovalsPage />} />
          <Route path={CLICK_AND_COLLECT_WAREHOUSES_ROUTE} element={<WarehouseManagementPage />} />
          <Route
            path={CLICK_AND_COLLECT_COLLECTION_POINTS_ROUTE}
            element={<CollectionPointManagementPage />}
          />
          <Route path={CLICK_AND_COLLECT_BAYS_ROUTE} element={<BayManagementPage />} />
          <Route path={CLICK_AND_COLLECT_GATE_CHECKIN_ROUTE} element={<GateCheckInPage />} />
          <Route path={DELIVERY_TYPES_ROUTE} element={<DeliveryTypesPage />} />
          <Route path={OTP_PROVIDERS_ROUTE} element={<OtpProvidersPage />} />
          <Route path={CLICK_AND_COLLECT_CNC_DASHBOARD_ROUTE} element={<CncDashboardPage />} />
          <Route path={FAST_MOVING_SKU_ROUTE} element={<FastMovingSkuPage />} />
          <Route path={ORDERS_TO_PICK_ROUTE} element={<OrdersToPickPage />} />
          <Route path={PACKED_ORDERS_ROUTE} element={<PackedOrdersPage />} />
          <Route
            path={PRODUCT_STOCK_SUBSCRIPTIONS_ROUTE}
            element={<ProductStockSubscriptionsPage />}
          />
        </Route>
      </Route>

      {/* Catch all */}
      <Route
        path="*"
        element={
          isLoggedIn ? (
            passwordExpired ? (
              <Navigate to={CHANGE_PASSWORD_ROUTE} replace />
            ) : (
              <Navigate to={DASHBOARD_ROUTE} replace />
            )
          ) : (
            <Navigate to={lOGIN_ROUTE} replace />
          )
        }
      />
    </Routes>
  );
};

export default RootRoutes;
